/* eslint-disable no-restricted-imports */
import {StaticHostConfig} from '@aboutyou/config';
import type {GrpcConfig} from '@aboutyou/data-fetcher';
// eslint-disable-next-line monorepo/no-internal-import
import {useStandaloneGrpcRequest} from '@aboutyou/data-fetcher/src/grpc/useStandaloneGrpcRequest'; // this avoids a circular dependency within Jest
import {RemoteConfigService_WebConfigLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/remote_config/service.grpc.lazy';
import {Log} from '@aboutyou/mlm';
// eslint-disable-next-line monorepo/no-internal-import
import {getVersionNameForDDTraces} from '@aboutyou/bootstrap/src/utils/setAppVersion';
// eslint-disable-next-line monorepo/no-internal-import
import {ServerInMemoryCache} from '@aboutyou/storage/src/nodeInMemoryCache/ServerInMemoryCache';
import {config as defaultConfig} from '../defaults';
import {extractFeatureFromEnvironment} from './extractFeatureFromEnvironment';
import {getUrlConfigFeatureFlagOverrides} from './getUrlConfigFeatureFlagOverrides';
import {
  MergedFeatureToggles,
  mergeFeatureFlagsConfigs,
} from './mergeFeatureFlagsConfigs';
import {FeatureToggleOverrides} from './types';
import {setFeatureFlagVersion} from './versionNumber';

const $1_MINUTE = 60 * 1000;
const $2_MINUTES = 2 * 60 * 1000;

/**
 * We randomize the TTL to avoid requests spikes to the BE and thereby to the firebase endpoint.
 * Firebase has a pretty tight rate limiting, so we need to be careful and avoid spikes.
 */
function getRandomTTL(): number {
  return Math.floor(Math.random() * ($2_MINUTES - $1_MINUTE + 1)) + $1_MINUTE;
}

interface WebConfig {
  featureFlags: {
    overrides: Partial<FeatureToggleOverrides>;
    version: string;
  };
}

interface WebConfigMerged {
  featureFlags: {
    mergedConfig: MergedFeatureToggles;
    version: string;
  };
}

const webConfigCache = new ServerInMemoryCache<WebConfig>({});
const CACHE_KEY = `web-configs`;

const envOverrides = extractFeatureFromEnvironment();

export async function getWebConfig({
  hostConfig,
  grpcConfig,
  searchParams,
}: {
  hostConfig: StaticHostConfig;
  grpcConfig: GrpcConfig;
  searchParams: URLSearchParams;
}): Promise<WebConfigMerged> {
  const urlOverrides = getUrlConfigFeatureFlagOverrides(searchParams);

  if (webConfigCache.has(CACHE_KEY)) {
    const rawConfig = webConfigCache.get(CACHE_KEY) as WebConfig;

    return {
      featureFlags: {
        mergedConfig: mergeFeatureFlagsConfigs({
          defaultConfig,
          overrides: rawConfig.featureFlags.overrides,
          urlOverrides,
        }),
        version: rawConfig.featureFlags.version,
      },
    };
  }

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const webConfigRequest = useStandaloneGrpcRequest(
      {
        method: RemoteConfigService_WebConfigLazy,
        params: {},
      },
      {
        hostConfig: {
          hostname: hostConfig.tadaridaUrl,
          legacyHostname: hostConfig.gripsUrl,
        },
        grpcConfig,
      },
    );
    const webConfig = await webConfigRequest();

    const rawConfig = {
      featureFlags: {
        overrides: JSON.parse(webConfig.featureFlags.stringifiedConfig),
        version: webConfig.featureFlags.version,
      },
    };
    webConfigCache.set(CACHE_KEY, rawConfig, {ttl: getRandomTTL()});

    process.env.FEATURE_FLAGS_VERSION = `v${webConfig.featureFlags.version}`;

    // set the feature flag version globally whenever its fresh, so that all traces can use it until the ttl lasts
    setFeatureFlagVersion(webConfig.featureFlags.version);
    Log.addAttributeToTransaction('version', getVersionNameForDDTraces());

    const mergedConfig = {
      featureFlags: {
        mergedConfig: mergeFeatureFlagsConfigs({
          defaultConfig,
          overrides: {
            ...rawConfig.featureFlags.overrides,
            ...envOverrides,
          },
          urlOverrides,
        }),
        version: webConfig.featureFlags.version,
      },
    };

    return mergedConfig as WebConfigMerged;
  } catch (error) {
    const message = `Could not fetch WebConfig from the server`;
    Log.error('@aboutyou/bootstrap', message, {error});

    const versionFail = 'fail';
    // set the feature flag version globally whenever its fresh, so that all traces can use it until the ttl lasts
    setFeatureFlagVersion(versionFail);

    /**
     * If this request fails, we still want to return the default config and don't want to crash the app.
     */
    return {
      featureFlags: {
        mergedConfig: mergeFeatureFlagsConfigs({
          defaultConfig,
          overrides: {},
          urlOverrides,
        }),
        version: versionFail,
      },
    };
  }
}
